import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { UserIcon, PlusIcon, PencilIcon, TrashIcon, EyeIcon, MagnifyingGlassIcon, ChevronDownIcon, ChevronRightIcon, DocumentTextIcon, CalendarIcon, MapPinIcon, BuildingOfficeIcon, XMarkIcon, PhoneIcon, LanguageIcon, BriefcaseIcon, TruckIcon, ExclamationTriangleIcon, ClockIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { createDatePasteHandler } from '../utils/dateUtils';
import { capitalizeName } from '../utils/nameUtils';
import { getCustomerPortalErrorMessage } from '../utils/apiErrorUtils';
import {
  fetchClaimantNameDuplicate,
  fetchClaimNumberDuplicate,
  showDuplicateAlert,
  requestDuplicateConfirm,
  runClaimantNameDuplicateCheck
} from '../utils/duplicateClaimChecks';
import DuplicateWarningModal from '../components/DuplicateWarningModal';
import AdjusterConfirmModal from '../components/AdjusterConfirmModal';
import { useViewExistingClaim } from '../hooks/useViewExistingClaim';
import {
  getJobStatusColor,
  getJobStatusLabel
} from '../utils/statusConstants';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Utility function to prevent Enter key from submitting forms accidentally
const preventEnterSubmit = (e) => {
  if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA' && e.target.type !== 'submit') {
    e.preventDefault();
    return false;
  }
};

// UI Components
const SearchableSelect = ({ value, onChange, options = [], placeholder, className = '', label, required = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options || []);
  const dropdownRef = useRef(null);

  // Initialize searchTerm with the selected option's label
  useEffect(() => {
    if (value && options && Array.isArray(options)) {
      const selectedOption = options.find(option => option.value === value);
      if (selectedOption) {
        setSearchTerm(selectedOption.label || '');
      }
    } else if (!value) {
      setSearchTerm('');
    }
  }, [value, options]);

  useEffect(() => {
    if (!options || !Array.isArray(options)) {
      setFilteredOptions([]);
      return;
    }
    
    const filtered = options.filter(option => 
      (option.label && option.label.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (option.value && String(option.value).toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredOptions(filtered);
  }, [searchTerm, options]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (option) => {
    if (option && option.value !== undefined) {
      onChange({ target: { value: option.value } });
      setSearchTerm(option.label || '');
      setIsOpen(false);
    }
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  return (
    <div className={`relative searchable-select ${className}`} ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
        </div>
        {isOpen && filteredOptions.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
            {filteredOptions.map((option) => (
              <div
                key={option.value}
                className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                onClick={() => handleSelect(option)}
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Claimants = () => {
  const { makeAuthenticatedRequest, customer } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pendingClaimantIdRef = useRef(null);
  const [claimants, setClaimants] = useState([]);
  const [sortedClaimants, setSortedClaimants] = useState([]);
  const [billingAccounts, setBillingAccounts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingClaimant, setEditingClaimant] = useState(null);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [selectedClaimantForClaim, setSelectedClaimantForClaim] = useState(null);
  const [editingClaim, setEditingClaim] = useState(null);
  const [expandedClaimant, setExpandedClaimant] = useState(null);
  const [mapsInitialized, setMapsInitialized] = useState(false);
  const [autocomplete, setAutocomplete] = useState(null);
  const autocompleteRef = useRef(null);
  const claimantNamesRef = useRef({ first_name: '', last_name: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [allScopeSearchClaimants, setAllScopeSearchClaimants] = useState([]);
  const [allScopeSearchLoading, setAllScopeSearchLoading] = useState(false);
  const [filteredClaimants, setFilteredClaimants] = useState([]);
  const [showClaimantDetailsModal, setShowClaimantDetailsModal] = useState(false);
  const [selectedClaimantForDetails, setSelectedClaimantForDetails] = useState(null);
  const [claimantDetailAppointments, setClaimantDetailAppointments] = useState([]);
  const [loadingClaimantAppointments, setLoadingClaimantAppointments] = useState(false);
  const [selectedClaimForJob, setSelectedClaimForJob] = useState(null);
  const [activeTab, setActiveTab] = useState('my'); // 'my' | 'all'
  // Track per-claimant claims loading status to avoid false "No claims" state
  const [claimsLoadingById, setClaimsLoadingById] = useState({});
  const [claimFormData, setClaimFormData] = useState({
    case_type: '',
    claim_number: '',
    date_of_injury: '',
    ct_start_date: '',
    ct_end_date: '',
    diagnosis: '',
    employer_insured: '',
    contact_claims_handler_id: '',
    adjusters_assistant_id: '',
    nurse_case_manager_id: '',
    field_case_manager_id: '',
    file_status: 'Open'
  });
  const [claimNumberWarning, setClaimNumberWarning] = useState(null);
  const [claimantNameWarning, setClaimantNameWarning] = useState(null);
  const [duplicateModal, setDuplicateModal] = useState(null);
  const {
    viewClaim,
    promptIfNeeded,
    pendingClaim,
    saveAssignment,
    skipPrompt,
    saving: savingRole
  } = useViewExistingClaim(makeAuthenticatedRequest, customer);
  
  // Pagination state for claimants list
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const genderOptions = [
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
    { value: 'Other', label: 'Other' },
    { value: 'Prefer not to say', label: 'Prefer not to say' }
  ];

  const caseTypeOptions = [
    { value: 'Workers Compensation', label: 'Workers Compensation' },
    { value: 'Personal Injury', label: 'Personal Injury' },
    { value: 'Medical Malpractice', label: 'Medical Malpractice' },
    { value: 'Auto Accident', label: 'Auto Accident' },
    { value: 'Slip and Fall', label: 'Slip and Fall' },
    { value: 'Product Liability', label: 'Product Liability' },
    { value: 'Other', label: 'Other' }
  ];
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    gender: '',
    date_of_birth: '',
    phone: '',
    language: '',
    customLanguage: '',
    billing_account_id: '',
    address: '',
    address_latitude: '',
    address_longitude: '',
    city: '',
    state: '',
    zip_code: '',
    date_of_injury: '',
    employer: '',
    examiner: '',
    is_active: true
  });

  // Helper functions for managing recently viewed claimants
  const getRecentlyViewedClaimants = () => {
    try {
      const stored = localStorage.getItem('recentlyViewedClaimantsPage');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  };

  const updateRecentlyViewedClaimant = (claimantId) => {
    try {
      const recent = getRecentlyViewedClaimants();
      recent[claimantId] = Date.now();
      // Keep only last 100 viewed claimants
      const sorted = Object.entries(recent)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 100);
      localStorage.setItem('recentlyViewedClaimantsPage', JSON.stringify(Object.fromEntries(sorted)));
      
      // Re-sort the current claimants list
      if (claimants.length > 0) {
        const resorted = sortClaimantsByRecentlyViewed(claimants);
        setSortedClaimants(resorted);
      }
    } catch (error) {
      console.error('Error updating recently viewed claimants:', error);
    }
  };

  const sortClaimantsByRecentlyViewed = useCallback((claimantsArray) => {
    const recentlyViewed = getRecentlyViewedClaimants();
    return [...claimantsArray].sort((a, b) => {
      const aTime = recentlyViewed[a.id] || 0;
      const bTime = recentlyViewed[b.id] || 0;
      
      // If both or neither have been viewed, keep original order
      if (aTime === bTime) {
        return 0;
      }
      
      // Most recent first
      return bTime - aTime;
    });
  }, []);

  const loadClaimants = useCallback(async () => {
    try {
      setLoading(true);
      setClaimants([]);
      setExpandedClaimant(null);
      const qs = activeTab === 'all' && formData.billing_account_id
        ? `?scope=all&billingAccountId=${formData.billing_account_id}`
        : '?scope=my';
      const data = await makeAuthenticatedRequest(`/claimants${qs}`);
      
      if (data.success) {
        setClaimants(data.data || []);
      } else {
        toast.error(data.message || 'Failed to load claimants');
      }
    } catch (error) {
      console.error('Error loading claimants:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to load claimants'));
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, activeTab, formData.billing_account_id]);

  // Sort claimants by recently viewed whenever claimants change
  useEffect(() => {
    if (claimants.length > 0) {
      const sorted = sortClaimantsByRecentlyViewed(claimants);
      setSortedClaimants(sorted);
    } else {
      setSortedClaimants([]);
    }
  }, [claimants, sortClaimantsByRecentlyViewed]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const claimantMatchesSearch = useCallback((claimant, term) => {
    const searchLower = term.toLowerCase();
    const fullName = `${claimant.first_name || ''} ${claimant.last_name || ''}`.toLowerCase();
    const name = claimant.name ? claimant.name.toLowerCase() : '';
    const phone = claimant.phone ? claimant.phone.toLowerCase() : '';
    const language = claimant.language ? claimant.language.toLowerCase() : '';
    const address = claimant.address ? claimant.address.toLowerCase() : '';
    const employer = claimant.employer ? claimant.employer.toLowerCase() : '';
    const claimNumbers = claimant.claims
      ? claimant.claims.map((claim) => claim.claim_number?.toLowerCase() || '').join(' ')
      : '';

    return (
      fullName.includes(searchLower) ||
      name.includes(searchLower) ||
      phone.includes(searchLower) ||
      language.includes(searchLower) ||
      address.includes(searchLower) ||
      employer.includes(searchLower) ||
      claimNumbers.includes(searchLower)
    );
  }, []);

  // Filter claimants based on search term (using sorted claimants)
  useEffect(() => {
    const claimantsToFilter = sortedClaimants.length > 0 ? sortedClaimants : claimants;
    
    if (!searchTerm.trim()) {
      setFilteredClaimants(claimantsToFilter);
    } else {
      const filtered = claimantsToFilter.filter((claimant) =>
        claimantMatchesSearch(claimant, searchTerm.trim())
      );
      setFilteredClaimants(filtered);
    }
    setPage(1);
  }, [sortedClaimants, claimants, searchTerm, claimantMatchesSearch]);

  useEffect(() => {
    let cancelled = false;

    async function searchAllScope() {
      if (activeTab !== 'my' || !debouncedSearchTerm.trim()) {
        setAllScopeSearchClaimants([]);
        setAllScopeSearchLoading(false);
        return;
      }

      const billingId = billingAccounts[0]?.id || formData.billing_account_id;
      if (!billingId) {
        setAllScopeSearchClaimants([]);
        return;
      }

      setAllScopeSearchLoading(true);
      const term = debouncedSearchTerm.trim();
      const myIds = new Set(claimants.map((c) => c.id));

      try {
        const [allRes, claimsRes] = await Promise.all([
          makeAuthenticatedRequest(`/claimants?scope=all&billingAccountId=${billingId}`),
          makeAuthenticatedRequest(
            `/claims?claim_number=${encodeURIComponent(term)}&billingAccountId=${billingId}&limit=30`
          )
        ]);

        const allList = allRes?.success ? allRes.data || [] : [];
        const matchingClaims = claimsRes?.success ? claimsRes.data || [] : [];
        const claimsByClaimantId = {};
        matchingClaims.forEach((claim) => {
          if (!claim.claimant_id) return;
          if (!claimsByClaimantId[claim.claimant_id]) {
            claimsByClaimantId[claim.claimant_id] = [];
          }
          claimsByClaimantId[claim.claimant_id].push(claim);
        });
        const claimantIdsFromClaims = new Set(
          Object.keys(claimsByClaimantId).map((id) => parseInt(id, 10))
        );

        const results = allList
          .filter((c) => !myIds.has(c.id))
          .filter(
            (c) =>
              claimantIdsFromClaims.has(c.id) || claimantMatchesSearch(c, term)
          )
          .map((c) =>
            claimsByClaimantId[c.id] ? { ...c, claims: claimsByClaimantId[c.id] } : c
          );

        if (!cancelled) setAllScopeSearchClaimants(results);
      } catch (error) {
        console.error('Error searching all-scope claimants:', error);
        if (!cancelled) setAllScopeSearchClaimants([]);
      } finally {
        if (!cancelled) setAllScopeSearchLoading(false);
      }
    }

    searchAllScope();
    return () => {
      cancelled = true;
    };
  }, [
    activeTab,
    debouncedSearchTerm,
    claimants,
    formData.billing_account_id,
    billingAccounts,
    makeAuthenticatedRequest,
    claimantMatchesSearch
  ]);

  const loadBillingAccounts = useCallback(async () => {
    try {
      const data = await makeAuthenticatedRequest('/billing-accounts');
      
      if (data.success) {
        setBillingAccounts(data.data || []);
        // Automatically set the billing account ID if not already set
        if (data.data && data.data.length > 0 && data.data[0] && !formData.billing_account_id) {
          setFormData(prev => ({
            ...prev,
            billing_account_id: data.data[0].id.toString()
          }));
        }
      }
    } catch (error) {
      console.error('Error loading billing accounts:', error);
    }
  }, [makeAuthenticatedRequest, formData.billing_account_id]);

  const loadCustomers = useCallback(async () => {
    try {
      const data = await makeAuthenticatedRequest('/customers');
      
      if (data.success) {
        setCustomers(data.data || []);
      }
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  }, [makeAuthenticatedRequest]);

  const loadLanguages = useCallback(async () => {
    try {
      const data = await makeAuthenticatedRequest('/parametric/languages');
      
      if (data.success) {
        setLanguages(data.data || []);
      }
    } catch (error) {
      console.error('Error loading languages:', error);
    }
  }, [makeAuthenticatedRequest]);

  useEffect(() => {
    loadClaimants();
    loadBillingAccounts();
    loadCustomers();
    loadLanguages();
    initializeGoogleMaps();
  }, [loadClaimants, loadBillingAccounts, loadCustomers, loadLanguages]);

  useEffect(() => {
    const param = searchParams.get('claimantId');
    if (param) {
      const parsed = parseInt(param, 10);
      if (Number.isInteger(parsed)) {
        pendingClaimantIdRef.current = parsed;
      }
    }
  }, [searchParams]);

  useEffect(() => {
    const claimantId = pendingClaimantIdRef.current;
    if (!claimantId || loading) return;

    const claimant = claimants.find((c) => c.id === claimantId);
    if (!claimant) {
      if (activeTab === 'my' && formData.billing_account_id) {
        setActiveTab('all');
        return;
      }
      pendingClaimantIdRef.current = null;
      return;
    }

    pendingClaimantIdRef.current = null;
    openClaimantDetailsModal(claimant);
    const next = new URLSearchParams(searchParams);
    next.delete('claimantId');
    setSearchParams(next, { replace: true });
  }, [loading, claimants, activeTab, formData.billing_account_id, searchParams, setSearchParams]);

  const initializeGoogleMaps = () => {
    if (window.google && window.google.maps) {
      setMapsInitialized(true);
    } else {
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      
      if (!apiKey) {
        console.warn('Google Maps API key not found. Address autocomplete will be disabled.');
        return;
      }
      
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => setMapsInitialized(true);
      script.onerror = () => {
        console.error('Failed to load Google Maps API');
      };
      document.head.appendChild(script);
    }
  };

  const setupAddressAutocomplete = (inputElement) => {
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      return;
    }

    // Prevent multiple initializations
    if (autocompleteRef.current) {
      return;
    }

    const autocompleteInstance = new window.google.maps.places.Autocomplete(inputElement, {
      types: ['address'],
      componentRestrictions: { country: 'us' }
    });

    autocompleteInstance.addListener('place_changed', () => {
      const place = autocompleteInstance.getPlace();
      
      if (place.geometry && place.geometry.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        
        setFormData(prev => ({
          ...prev,
          address: place.formatted_address || '',
          address_latitude: lat.toString(),
          address_longitude: lng.toString()
        }));
      }
    });

    autocompleteRef.current = autocompleteInstance;
    setAutocomplete(autocompleteInstance);
  };

  const loadClaimantWithClaims = useCallback(async (claimantId, claimantObj) => {
    try {
      const scope = activeTab === 'all' ? 'all' : 'my';
      const data = await makeAuthenticatedRequest(
        `/claimants/${encodeURIComponent(claimantId)}/claims?scope=${scope}`
      );
       
      if (data.success) {
        return data.data;
      }
    } catch (error) {
      console.error('Error loading claimant details:', error);
    }
    return null;
  }, [makeAuthenticatedRequest, activeTab]);

  const toggleClaimantExpansion = useCallback(async (claimant) => {
    if (expandedClaimant && expandedClaimant.id === claimant.id) {
      setExpandedClaimant(null);
    } else {
      // Track that this claimant was viewed when expanding
      updateRecentlyViewedClaimant(claimant.id);
      
      setExpandedClaimant(claimant);
      
      // Load claims if not already loaded
      if (!claimant.claims) {
        setClaimsLoadingById(prev => ({ ...prev, [claimant.id]: true }));
        const claimantWithClaims = await loadClaimantWithClaims(claimant.id, claimant);
        if (claimantWithClaims) {
          setClaimants(prev => prev.map(c => 
            c.id === claimant.id 
              ? { ...c, claims: claimantWithClaims }
              : c
          ));
        }
        setClaimsLoadingById(prev => ({ ...prev, [claimant.id]: false }));
      }
    }
  }, [expandedClaimant, loadClaimantWithClaims]);

  const openClaimantDetailsModal = async (claimant) => {
    // Track that this claimant was viewed
    updateRecentlyViewedClaimant(claimant.id);
    
    setSelectedClaimantForDetails(claimant);
    setShowClaimantDetailsModal(true);
    setClaimantDetailAppointments([]);
    
    // Load claims if not already loaded
    if (!claimant.claims) {
      const claimantWithClaims = await loadClaimantWithClaims(claimant.id, claimant);
      if (claimantWithClaims) {
        // Update the claimant in the list with claims
        setClaimants(prev => prev.map(c => 
          c.id === claimant.id 
            ? { ...c, claims: claimantWithClaims }
            : c
        ));
        // Update the selected claimant for the modal
        setSelectedClaimantForDetails(prev => ({ ...prev, claims: claimantWithClaims }));
      }
    }

    // Load past + upcoming appointments for this claimant (same billing account allowed)
    try {
      setLoadingClaimantAppointments(true);
      const data = await makeAuthenticatedRequest(
        `/appointments?claimantId=${claimant.id}&limit=50`
      );
      if (data && data.success) {
        setClaimantDetailAppointments(data.data?.appointments || []);
      }
    } catch (error) {
      console.error('Error loading claimant appointments:', error);
      setClaimantDetailAppointments([]);
    } finally {
      setLoadingClaimantAppointments(false);
    }
  };

  const closeClaimantDetailsModal = () => {
    setShowClaimantDetailsModal(false);
    setSelectedClaimantForDetails(null);
    setSelectedClaimForJob(null);
    setClaimantDetailAppointments([]);
  };

  const splitClaimantAppointments = (appointments) => {
    const now = new Date();
    const upcomingStatuses = new Set([
      'finding_interpreter',
      'finding_driver',
      'assigned',
      'in_progress',
      'reminders_sent',
      'authorize_rates',
      'pending_authorization',
      'authorized',
      'requested'
    ]);
    const upcoming = [];
    const past = [];
    for (const apt of appointments || []) {
      const dateStr = apt.scheduled_date;
      let scheduled = null;
      if (dateStr) {
        if (typeof dateStr === 'string' && dateStr.includes('-') && !dateStr.includes('T')) {
          const [year, month, day] = dateStr.split('-').map(Number);
          const timeParts = (apt.scheduled_time || '00:00:00').toString().split(':');
          scheduled = new Date(
            year,
            month - 1,
            day,
            parseInt(timeParts[0] || '0', 10),
            parseInt(timeParts[1] || '0', 10)
          );
        } else {
          scheduled = new Date(dateStr);
        }
      }
      const isUpcoming =
        scheduled &&
        scheduled >= now &&
        upcomingStatuses.has(String(apt.status || '').toLowerCase());
      if (isUpcoming) upcoming.push(apt);
      else past.push(apt);
    }
    return { upcoming, past };
  };

  const formatAppointmentDateTime = (apt) => {
    const dateStr = apt.scheduled_date;
    let dateLabel = 'Not scheduled';
    if (dateStr) {
      if (typeof dateStr === 'string' && dateStr.includes('-') && !dateStr.includes('T')) {
        const [year, month, day] = dateStr.split('-').map(Number);
        dateLabel = new Date(year, month - 1, day).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });
      } else {
        dateLabel = new Date(dateStr).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });
      }
    }
    let timeLabel = null;
    if (apt.scheduled_time) {
      timeLabel = new Date(`2000-01-01T${apt.scheduled_time}`).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
    return { dateLabel, timeLabel };
  };

  const renderClaimantAppointmentRow = (apt) => {
    const { dateLabel, timeLabel } = formatAppointmentDateTime(apt);
    return (
      <div
        key={apt.id}
        onClick={() => navigate(`/appointments/${apt.id}`)}
        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors border border-gray-200"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-2">
            <span className="font-medium text-gray-900 text-sm">
              {apt.job_number || `Appointment #${apt.id}`}
            </span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getJobStatusColor(apt)}`}>
              {getJobStatusLabel(apt)}
            </span>
            {apt.claim_number && (
              <span className="text-xs text-gray-500">{apt.claim_number}</span>
            )}
          </div>
          <div className="mt-1 flex items-center text-sm text-gray-600">
            <CalendarIcon className="h-4 w-4 mr-1 flex-shrink-0" />
            <span>{dateLabel}</span>
            {timeLabel && (
              <>
                <ClockIcon className="h-4 w-4 ml-3 mr-1 flex-shrink-0" />
                <span>{timeLabel}</span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const handleViewExistingClaimant = async (claimantId, matchData = null) => {
    const id = parseInt(claimantId, 10);
    if (!id) return;

    setShowModal(false);
    setClaimantNameWarning(null);

    const existing = claimants.find((c) => c.id === id);
    if (existing) {
      await openClaimantDetailsModal(existing);
      return;
    }

    const match = matchData || duplicateModal?.data?.matches?.find((m) => m.id === id);
    if (match) {
      await openClaimantDetailsModal(match);
    }
  };

  const requestJobForClaimant = (claimant, claim = null, jobType = 'interpreting') => {
    // Navigate to the appropriate job creation page with the claimant and claim pre-selected
    const baseUrl = jobType === 'transportation' ? '/transportation/new' : '/appointments/new';
    const billingQuery = activeTab === 'all'
      ? `&billingAccountId=${encodeURIComponent(formData.billing_account_id || claimant.billing_account_id || '')}`
      : '';
    const url = claim 
      ? `${baseUrl}?claimantId=${claimant.id}&claimId=${claim.id}${billingQuery}`
      : `${baseUrl}?claimantId=${claimant.id}${billingQuery}`;
    navigate(url);
  };

  const selectClaimForJob = (claim) => {
    setSelectedClaimForJob(claim);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'first_name' || field === 'last_name') {
        claimantNamesRef.current = {
          first_name: next.first_name,
          last_name: next.last_name
        };
      }
      return next;
    });
    if (field === 'first_name' || field === 'last_name') {
      setClaimantNameWarning(null);
    }
  };

  const checkClaimantNameOnBlur = async (firstName, lastName) => {
    const fn = (firstName ?? claimantNamesRef.current.first_name ?? formData.first_name ?? '').trim();
    const ln = (lastName ?? claimantNamesRef.current.last_name ?? formData.last_name ?? '').trim();
    if (!fn || !ln) return;

    await runClaimantNameDuplicateCheck(
      makeAuthenticatedRequest,
      setDuplicateModal,
      setClaimantNameWarning,
      fn,
      ln,
      editingClaimant?.id
    );
  };

  const resetForm = () => {
    setClaimantNameWarning(null);
    claimantNamesRef.current = { first_name: '', last_name: '' };
    setFormData({
      first_name: '',
      last_name: '',
      gender: '',
      date_of_birth: '',
      phone: '',
      language: '',
      billing_account_id: billingAccounts && billingAccounts.length > 0 && billingAccounts[0] ? billingAccounts[0].id.toString() : '',
      address: '',
      address_latitude: '',
      address_longitude: '',
      city: '',
      state: '',
      zip_code: '',
      date_of_injury: '',
      employer: '',
      examiner: '',
      is_active: true
    });
    setEditingClaimant(null);
  };

  useEffect(() => {
    if (!showModal || editingClaimant) return;

    const fn = (claimantNamesRef.current.first_name || formData.first_name || '').trim();
    const ln = (claimantNamesRef.current.last_name || formData.last_name || '').trim();
    if (!fn || !ln) return;

    const timer = setTimeout(() => {
      checkClaimantNameOnBlur(fn, ln);
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.first_name, formData.last_name, showModal, editingClaimant]);

  const openCreateModal = () => {
    // Reset autocomplete ref for new claimant
    autocompleteRef.current = null;
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (claimant) => {
    // Reset autocomplete ref for new claimant
    autocompleteRef.current = null;
    // Check if the language exists in the languages list
    const languageExists = languages.some(lang => lang.name === claimant.language);
    const languageValue = languageExists ? claimant.language : (claimant.language ? 'other' : '');
    const customLanguageValue = languageExists ? '' : (claimant.language || '');
    
    claimantNamesRef.current = {
      first_name: claimant.first_name || '',
      last_name: claimant.last_name || ''
    };
    setFormData({
      first_name: claimant.first_name || '',
      last_name: claimant.last_name || '',
      gender: claimant.gender || '',
      date_of_birth: claimant.date_of_birth ? claimant.date_of_birth.split('T')[0] : '',
      phone: claimant.phone || '',
      language: languageValue,
      customLanguage: customLanguageValue,
      billing_account_id: claimant.billing_account_id ? claimant.billing_account_id.toString() : '',
      address: claimant.address || '',
      address_latitude: claimant.address_latitude || '',
      address_longitude: claimant.address_longitude || '',
      city: claimant.city || '',
      state: claimant.state || '',
      zip_code: claimant.zip_code || '',
      date_of_injury: claimant.date_of_injury ? claimant.date_of_injury.split('T')[0] : '',
      employer: claimant.employer || '',
      examiner: claimant.examiner || '',
      is_active: claimant.is_active !== undefined ? claimant.is_active : true
    });
    setEditingClaimant(claimant);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    // Reset autocomplete ref when closing modal
    autocompleteRef.current = null;
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (!editingClaimant) {
        const claimantDup = await fetchClaimantNameDuplicate(
          makeAuthenticatedRequest,
          formData.first_name,
          formData.last_name
        );
        setClaimantNameWarning(claimantDup);
        if (!(await requestDuplicateConfirm(setDuplicateModal, 'claimant', claimantDup))) {
          return;
        }
      }

      const url = editingClaimant 
        ? `/claimants/${editingClaimant.id}`
        : `/claimants`;
      
      const method = editingClaimant ? 'PUT' : 'POST';
      
      // Prepare data: use customLanguage if "other" is selected
      const dataToSend = {
        ...formData,
        language: formData.language === 'other' ? formData.customLanguage : formData.language
      };
      // Remove customLanguage from the payload as it's not needed in the backend
      delete dataToSend.customLanguage;
      
      const data = await makeAuthenticatedRequest(url, {
        method,
        body: JSON.stringify(dataToSend)
      });
      
      if (data.success) {
        toast.success(editingClaimant ? 'Claimant updated successfully' : 'Claimant created successfully');
        closeModal();
        await loadClaimants();
        
        // If creating a new claimant, open the details modal
        if (!editingClaimant && data.data && data.data.id) {
          // Find the newly created claimant and open its details modal
          const newClaimant = data.data;
          await openClaimantDetailsModal(newClaimant);
        }
      } else {
        toast.error(data.message || 'Operation failed');
      }
    } catch (error) {
      console.error('Error saving claimant:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to save claimant'));
    }
  };

  const handleDelete = async (claimantId) => {
    if (!window.confirm('Are you sure you want to delete this claimant?')) {
      return;
    }

    try {
      const data = await makeAuthenticatedRequest(
        `/claimants/${claimantId}`,
        { method: 'DELETE' }
      );
      
      if (data.success) {
        toast.success('Claimant deleted successfully');
        loadClaimants();
      } else {
        toast.error(data.message || 'Failed to delete claimant');
      }
    } catch (error) {
      console.error('Error deleting claimant:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to delete claimant'));
    }
  };

  const openCreateClaimModal = (claimant = null) => {
    setSelectedClaimantForClaim(claimant);
    setEditingClaim(null);
    setClaimFormData({
      case_type: '',
      claim_number: '',
      date_of_injury: '',
      ct_start_date: '',
      ct_end_date: '',
      diagnosis: '',
      employer_insured: '',
      contact_claims_handler_id: '',
      adjusters_assistant_id: '',
      nurse_case_manager_id: '',
      field_case_manager_id: '',
      file_status: 'Open',
      claimant_id: ''
    });
    setClaimNumberWarning(null);
    setShowClaimModal(true);
  };

  const openEditClaimModal = (claim) => {
    setEditingClaim(claim);
    setSelectedClaimantForClaim(null);
    setClaimFormData({
      case_type: claim.case_type || '',
      claim_number: claim.claim_number || '',
      date_of_injury: claim.date_of_injury ? claim.date_of_injury.split('T')[0] : '',
      ct_start_date: claim.ct_start_date ? claim.ct_start_date.split('T')[0] : '',
      ct_end_date: claim.ct_end_date ? claim.ct_end_date.split('T')[0] : '',
      diagnosis: claim.diagnosis || '',
      employer_insured: claim.employer_insured || '',
      contact_claims_handler_id: claim.contact_claims_handler_id ? claim.contact_claims_handler_id.toString() : '',
      adjusters_assistant_id: claim.adjusters_assistant_id ? claim.adjusters_assistant_id.toString() : '',
      nurse_case_manager_id: claim.nurse_case_manager_id ? claim.nurse_case_manager_id.toString() : '',
      field_case_manager_id: claim.field_case_manager_id ? claim.field_case_manager_id.toString() : ''
    });
    setClaimNumberWarning(null);
    setShowClaimModal(true);
  };

  const closeClaimModal = () => {
    setShowClaimModal(false);
    setSelectedClaimantForClaim(null);
    setEditingClaim(null);
    setClaimFormData({
      case_type: '',
      claim_number: '',
      date_of_injury: '',
      ct_start_date: '',
      ct_end_date: '',
      diagnosis: '',
      employer_insured: '',
      contact_claims_handler_id: '',
      adjusters_assistant_id: '',
      nurse_case_manager_id: '',
      field_case_manager_id: '',
      file_status: 'Open'
    });
    setClaimNumberWarning(null);
  };

  const checkClaimNumberOnBlur = async () => {
    const num = (claimFormData.claim_number || '').trim();
    if (!num) { setClaimNumberWarning(null); return; }
    const claim = await fetchClaimNumberDuplicate(
      makeAuthenticatedRequest,
      num,
      editingClaim?.id
    );
    setClaimNumberWarning(claim);
    showDuplicateAlert(setDuplicateModal, 'claim', claim);
  };

  const handleClaimSubmit = async (e) => {
    e.preventDefault();

    if (!editingClaim && (!claimFormData.contact_claims_handler_id || !claimFormData.adjusters_assistant_id)) {
      toast.error('Contact/claims handler and adjuster\'s assistant are required');
      return;
    }
    
    try {
      const claimDup = await fetchClaimNumberDuplicate(
        makeAuthenticatedRequest,
        claimFormData.claim_number,
        editingClaim?.id
      );
      setClaimNumberWarning(claimDup);
      if (!(await requestDuplicateConfirm(setDuplicateModal, 'claim', claimDup))) {
        return;
      }

      const url = editingClaim 
        ? `/claims/${editingClaim.id}`
        : `/claims`;
      
      const requestBody = editingClaim 
        ? claimFormData 
        : { 
            ...claimFormData, 
            claimant_id: selectedClaimantForClaim ? selectedClaimantForClaim.id : null 
          };
      
      const data = await makeAuthenticatedRequest(url, {
        method: editingClaim ? 'PUT' : 'POST',
        body: JSON.stringify(requestBody)
      });
      
      if (data.success) {
        toast.success(editingClaim ? 'Claim updated successfully' : 'Claim created successfully');
        closeClaimModal();
        
        // Refresh the expanded claimant's claims if claim was associated with a claimant
        if (expandedClaimant && requestBody.claimant_id === expandedClaimant.id) {
          const updatedClaimant = await loadClaimantWithClaims(expandedClaimant.id, expandedClaimant);
          if (updatedClaimant) {
            setClaimants(prev => prev.map(c => 
              c.id === expandedClaimant.id 
                ? { ...c, claims: updatedClaimant }
                : c
            ));
          }
        }
        
        // If the details modal is open, refresh its claims too
        if (selectedClaimantForDetails && requestBody.claimant_id && selectedClaimantForDetails.id === requestBody.claimant_id) {
          const updatedClaimantWithClaims = await loadClaimantWithClaims(selectedClaimantForDetails.id, selectedClaimantForDetails);
          if (updatedClaimantWithClaims) {
            setSelectedClaimantForDetails(prev => ({ ...prev, claims: updatedClaimantWithClaims }));
            setClaimants(prev => prev.map(c => 
              c.id === selectedClaimantForDetails.id 
                ? { ...c, claims: updatedClaimantWithClaims }
                : c
            ));
          }
        }
      } else {
        toast.error(data.message || 'Failed to save claim');
      }
    } catch (error) {
      console.error('Error saving claim:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to save claim'));
    }
  };

  const handleDeleteClaim = async (claimId) => {
    if (!window.confirm('Are you sure you want to delete this claim? This action cannot be undone.')) {
      return;
    }

    try {
      const data = await makeAuthenticatedRequest(
        `/claims/${claimId}`,
        { method: 'DELETE' }
      );
      
      if (data.success) {
        toast.success('Claim deleted successfully');
        
        // Refresh the expanded claimant's claims
        if (expandedClaimant) {
          const updatedClaimant = await loadClaimantWithClaims(expandedClaimant.id, expandedClaimant);
          if (updatedClaimant) {
            setClaimants(prev => prev.map(c => 
              c.id === expandedClaimant.id 
                ? { ...c, claims: updatedClaimant }
                : c
            ));
          }
        }
      } else {
        toast.error(data.message || 'Failed to delete claim');
      }
    } catch (error) {
      console.error('Error deleting claim:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to delete claim'));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      // Extract date parts to avoid timezone conversion
      const datePart = dateString.split('T')[0];
      const [year, month, day] = datePart.split('-').map(num => parseInt(num, 10));
      
      // Create date in local timezone (no UTC conversion)
      const localDate = new Date(year, month - 1, day);
      
      if (isNaN(localDate.getTime())) {
        return '';
      }
      
      return localDate.toLocaleDateString();
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  const formatDateOfBirth = (dob) => {
    if (!dob) return '';
    try {
      const datePart = (typeof dob === 'string' ? dob : '').split('T')[0];
      if (!datePart || !/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return '';
      const [year, month, day] = datePart.split('-').map((n) => parseInt(n, 10));
      return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
    } catch {
      return '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading claimants...</p>
        </div>
      </div>
    );
  }

  // Compute paginated slice of filtered claimants
  const totalClaimants = filteredClaimants.length;
  const totalPages = Math.max(1, Math.ceil(totalClaimants / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedClaimants = filteredClaimants.slice(startIndex, endIndex);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl py-6 px-4 sm:px-6 lg:px-8">
        {/* Enhanced Header */}
        <div className="mb-8">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl shadow-lg p-8 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-bold text-white mb-2">Claimants</h1>
                <p className="text-purple-100 text-lg">
                  Manage claimants in your billing account
                </p>
              </div>
              <button
                onClick={openCreateModal}
                className="bg-white text-purple-600 px-6 py-3 rounded-lg hover:bg-purple-50 flex items-center font-semibold transition-colors"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Add Claimant
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 shadow-sm">
            <button
              onClick={() => {
                setActiveTab('my');
                setSearchTerm('');
                setShowClaimantDetailsModal(false);
                setSelectedClaimantForDetails(null);
                setSelectedClaimForJob(null);
              }}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === 'my'
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              My claims
            </button>
            <button
              onClick={() => {
                setActiveTab('all');
                setShowClaimantDetailsModal(false);
                setSelectedClaimantForDetails(null);
                setSelectedClaimForJob(null);
              }}
              className={`ml-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === 'all'
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              All claims
            </button>
          </div>
        </div>

        {/* Enhanced Search Bar */}
        <div className="mb-6 bg-white rounded-xl shadow-lg border border-gray-100 p-6">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-6 w-6 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search claimants by name, phone, language, address, employer, or claim number..."
              className="block w-full pl-12 pr-12 py-4 border border-gray-300 rounded-xl leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-lg"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            )}
          </div>
          {searchTerm && (
            <p className="mt-3 text-sm text-gray-600 flex items-center flex-wrap gap-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                {filteredClaimants.length}
              </span>
              <span>
                of {claimants.length} claimants match
                {activeTab === 'my' ? ' in My claims' : ' your search'}
              </span>
              {activeTab === 'my' && allScopeSearchClaimants.length > 0 && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                  +{allScopeSearchClaimants.length} in All claims
                </span>
              )}
            </p>
          )}
        </div>

        {/* Claimants List */}
        {filteredClaimants.length === 0 && !(activeTab === 'my' && allScopeSearchClaimants.length > 0) ? (
          <div className="text-center py-12">
            <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {searchTerm ? 'No claimants found' : 'No claimants'}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm 
                ? `No claimants match "${searchTerm}" in My claims.`
                : 'Get started by creating a new claimant.'
              }
            </p>
            {allScopeSearchLoading && searchTerm && activeTab === 'my' && (
              <p className="mt-2 text-sm text-gray-500">Checking All claims...</p>
            )}
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="mt-4 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Clear search
              </button>
            )}
          </div>
        ) : filteredClaimants.length === 0 && activeTab === 'my' && allScopeSearchClaimants.length > 0 ? (
          <div className="text-center py-6 text-gray-500 bg-white rounded-lg border border-gray-200 mb-4">
            <p className="text-sm">
              No matches for &ldquo;{debouncedSearchTerm}&rdquo; in <strong>My claims</strong>.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {paginatedClaimants.map((claimant) => (
              <div key={claimant.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer" onClick={() => openClaimantDetailsModal(claimant)}>
                {/* Claimant Header */}
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleClaimantExpansion(claimant);
                        }}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        {expandedClaimant && expandedClaimant.id === claimant.id ? (
                          <ChevronDownIcon className="h-5 w-5" />
                        ) : (
                          <ChevronRightIcon className="h-5 w-5" />
                        )}
                      </button>
                      
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {claimant.first_name && claimant.last_name 
                            ? `${claimant.first_name} ${claimant.last_name}`
                            : claimant.name || 'Unnamed Claimant'
                          }
                        </h3>
                        <div className="flex items-center space-x-4 mt-1 text-sm text-gray-600">
                          {claimant.gender && <span>{claimant.gender}</span>}
                          {claimant.date_of_birth && (
                            <span className="flex items-center">
                              <CalendarIcon className="h-4 w-4 mr-1" />
                              {formatDateOfBirth(claimant.date_of_birth)}
                            </span>
                          )}
                          {claimant.claims && claimant.claims.length > 0 && (
                            <span className="flex items-center">
                              <DocumentTextIcon className="h-4 w-4 mr-1" />
                              {claimant.claims.length} claim{claimant.claims.length !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openCreateClaimModal(claimant);
                        }}
                        className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <PlusIcon className="h-4 w-4 mr-1" />
                        Add Claim
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditModal(claimant);
                        }}
                        className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors duration-200"
                        title="Edit Claimant"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(claimant.id);
                        }}
                        className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-md transition-colors duration-200"
                        title="Delete Claimant"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Claimant Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                    {claimant.phone && (
                      <div className="flex items-center">
                        <span className="text-sm text-gray-600">Phone: {claimant.phone}</span>
                      </div>
                    )}
                    
                    {claimant.language && (
                      <div className="flex items-center">
                        <span className="text-sm text-gray-600">Language: {claimant.language}</span>
                      </div>
                    )}
                    
                    {claimant.billing_account_name && (
                      <div className="flex items-center">
                        <BuildingOfficeIcon className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                        <p className="text-sm text-gray-600">{claimant.billing_account_name}</p>
                      </div>
                    )}
                    
                    {claimant.address && (
                      <div className="flex items-center">
                        <MapPinIcon className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                        <div className="text-sm text-gray-600">
                          <p className="truncate">{claimant.address}</p>
                          {claimant.address_latitude && claimant.address_longitude && (
                            <p className="text-xs text-gray-400">
                              {claimant.address_latitude}, {claimant.address_longitude}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    
                  </div>
                </div>
                
                {/* Claims Section */}
                {expandedClaimant && expandedClaimant.id === claimant.id && (
                  <div className="border-t border-gray-200 bg-gray-50">
                    <div className="p-6">
                      <h4 className="text-md font-semibold text-gray-900 mb-4">Claims</h4>
                      
                      {claimsLoadingById[claimant.id] ? (
                        <div className="text-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                          <p className="mt-2 text-sm text-gray-500">Loading claims...</p>
                        </div>
                      ) : Array.isArray(claimant.claims) && claimant.claims.length > 0 ? (
                        <div className="space-y-3">
                          {claimant.claims.map((claim) => (
                            <div key={claim.id} className="bg-white rounded-lg p-4 border border-gray-200">
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-4">
                                    <h5 className="font-medium text-gray-900">{claim.claim_number}</h5>
                                    <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                                      {claim.case_type}
                                    </span>
                                  </div>
                                  {claim.date_of_injury && (
                                    <p className="text-sm text-gray-600 mt-1">
                                      Date of Injury: {formatDate(claim.date_of_injury)}
                                    </p>
                                  )}
                                  {(claim.ct_start_date || claim.ct_end_date) && (
                                    <p className="text-sm text-gray-600 mt-1">
                                      CT Period: {formatDate(claim.ct_start_date) || 'N/A'} - {formatDate(claim.ct_end_date) || 'N/A'}
                                    </p>
                                  )}
                                  {claim.diagnosis && (
                                    <p className="text-sm text-gray-600 mt-1">
                                      Diagnosis: {claim.diagnosis}
                                    </p>
                                  )}
                                  {claim.billing_account_name && (
                                    <p className="text-sm text-gray-600 mt-1">
                                      Billing Account: {claim.billing_account_name}
                                    </p>
                                  )}
                                  {claim.contact_claims_handler_name && (
                                    <p className="text-sm text-gray-600 mt-1">
                                      Contact: {claim.contact_claims_handler_name}
                                    </p>
                                  )}
                                  {claim.adjusters_assistant_name && (
                                    <p className="text-sm text-gray-600 mt-1">
                                      Assistant: {claim.adjusters_assistant_name}
                                    </p>
                                  )}
                                </div>
                                
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => openEditClaimModal(claim)}
                                    className="text-gray-600 hover:text-gray-900"
                                    title="Edit Claim"
                                  >
                                    <PencilIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteClaim(claim.id)}
                                    className="text-red-600 hover:text-red-900"
                                    title="Delete Claim"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <DocumentTextIcon className="mx-auto h-8 w-8 text-gray-400" />
                          <p className="mt-2 text-sm text-gray-500">No claims found for this claimant</p>
                          <button
                            onClick={(e) => { e.stopPropagation(); openCreateClaimModal(claimant); }}
                            className="mt-2 inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            Add First Claim
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
            {/* Pagination Controls */}
            <div className="flex items-center justify-between pt-4">
              <div className="text-sm text-gray-600">
                Showing {startIndex + 1}-{Math.min(endIndex, totalClaimants)} of {totalClaimants}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                  className={`px-3 py-1 rounded-md border text-sm ${currentPage <= 1 ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  className={`px-3 py-1 rounded-md border text-sm ${currentPage >= totalPages ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                >
                  Next
                </button>
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(1); }}
                  className="ml-2 px-2 py-1 border border-gray-300 rounded-md text-sm bg-white"
                >
                  <option value={10}>10 / page</option>
                  <option value={20}>20 / page</option>
                  <option value={50}>50 / page</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* All Claims matches when searching from My claims tab */}
        {!loading && activeTab === 'my' && debouncedSearchTerm && allScopeSearchClaimants.length > 0 && (
          <div className="mt-6 bg-white rounded-lg shadow-sm border border-amber-100 overflow-hidden">
            <div className="px-6 py-4 bg-amber-50 border-b border-amber-100">
              <p className="text-sm font-medium text-amber-900">
                Found in All Claims
              </p>
              <p className="text-xs text-amber-800 mt-1">
                These claimants match your search on your billing account but you are not the assigned
                contact handler or assistant on their claims. You can still view their details.
              </p>
            </div>
            <div className="divide-y divide-gray-100">
              {allScopeSearchClaimants.map((claimant) => (
                <div
                  key={claimant.id}
                  className="p-6 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => openClaimantDetailsModal(claimant)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {claimant.first_name && claimant.last_name
                          ? `${claimant.first_name} ${claimant.last_name}`
                          : claimant.name || 'Unnamed Claimant'}
                      </h3>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-gray-600">
                        {claimant.phone && <span>Phone: {claimant.phone}</span>}
                        {claimant.language && <span>Language: {claimant.language}</span>}
                        {claimant.billing_account_name && (
                          <span className="flex items-center">
                            <BuildingOfficeIcon className="h-4 w-4 text-gray-400 mr-1 flex-shrink-0" />
                            {claimant.billing_account_name}
                          </span>
                        )}
                      </div>
                      {Array.isArray(claimant.claims) && claimant.claims.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {claimant.claims.map((claim) => (
                            <span
                              key={claim.id}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {claim.claim_number}
                              {claim.case_type ? ` · ${claim.case_type}` : ''}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openClaimantDetailsModal(claimant);
                      }}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <EyeIcon className="h-4 w-4 mr-1" />
                      View
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {activeTab === 'my' && debouncedSearchTerm && allScopeSearchLoading && allScopeSearchClaimants.length === 0 && (
          <p className="mt-4 text-sm text-gray-500 text-center">Checking All claims...</p>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingClaimant ? 'Edit Claimant' : 'Add New Claimant'}
              </h3>
              
              <form onSubmit={handleSubmit} onKeyDown={preventEnterSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">First Name *</label>
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => handleInputChange('first_name', e.target.value)}
                      onBlur={(e) => {
                        const firstName = capitalizeName(e.target.value);
                        claimantNamesRef.current.first_name = firstName;
                        handleInputChange('first_name', firstName);
                        checkClaimantNameOnBlur(firstName, claimantNamesRef.current.last_name);
                      }}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Last Name *</label>
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => handleInputChange('last_name', e.target.value)}
                      onBlur={(e) => {
                        const lastName = capitalizeName(e.target.value);
                        claimantNamesRef.current.last_name = lastName;
                        handleInputChange('last_name', lastName);
                        checkClaimantNameOnBlur(claimantNamesRef.current.first_name, lastName);
                      }}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <SearchableSelect
                    label="Gender"
                    value={formData.gender}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                    options={genderOptions}
                    placeholder="Select gender"
                  />
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
                    <input
                      type="date"
                      value={formData.date_of_birth}
                      onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                      onPaste={createDatePasteHandler((e) => handleInputChange('date_of_birth', e.target.value))}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <div>
                      <SearchableSelect
                        label="Language"
                        value={formData.language}
                        onChange={(e) => {
                          handleInputChange('language', e.target.value);
                          // Clear custom language if not "other"
                          if (e.target.value !== 'other') {
                            handleInputChange('customLanguage', '');
                          }
                        }}
                        options={[
                          ...languages.filter(lang => lang.name.toLowerCase() !== 'agency').map(lang => ({ 
                            value: lang.name, 
                            label: lang.name 
                          })),
                          { value: 'other', label: 'Other' }
                        ]}
                        placeholder="Select preferred language"
                      />
                      {formData.language === 'other' && (
                        <div className="mt-2">
                          <input
                            type="text"
                            value={formData.customLanguage}
                            onChange={(e) => handleInputChange('customLanguage', e.target.value)}
                            placeholder="Please specify the language"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Billing Account</label>
                    <input
                      type="text"
                      value={
                        (editingClaimant && editingClaimant.billing_account_name)
                          ? editingClaimant.billing_account_name
                          : (billingAccounts && billingAccounts.length > 0 && billingAccounts[0]
                              ? billingAccounts[0].name
                              : 'Loading...')
                      }
                      disabled
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-100 text-gray-600 cursor-not-allowed"
                    />
                    <p className="mt-1 text-xs text-gray-500">Automatically set to your billing account</p>
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      placeholder={mapsInitialized ? "Start typing address for autocomplete..." : "Enter address manually"}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      ref={(input) => {
                        if (input && mapsInitialized && !autocompleteRef.current) {
                          setupAddressAutocomplete(input);
                        }
                      }}
                    />
                    {(formData.address_latitude && formData.address_longitude) && (
                      <div className="mt-2 text-xs text-gray-500">
                        Coordinates: {formData.address_latitude}, {formData.address_longitude}
                      </div>
                    )}
                  </div>
                  
</div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    {editingClaimant ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Claim Creation Modal */}
      {showClaimModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[60]">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingClaim ? 'Edit Claim' : 'Add New Claim'}
                {selectedClaimantForClaim && !editingClaim && (
                  <span className="block text-sm text-gray-600 mt-1">
                    for {selectedClaimantForClaim.first_name && selectedClaimantForClaim.last_name 
                      ? `${selectedClaimantForClaim.first_name} ${selectedClaimantForClaim.last_name}`
                      : selectedClaimantForClaim.name || 'Unnamed Claimant'
                    }
                  </span>
                )}
              </h3>
              
              <form onSubmit={handleClaimSubmit} onKeyDown={preventEnterSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Claimant Selection - Optional */}
                  {!selectedClaimantForClaim && !editingClaim && (
                    <div className="md:col-span-2">
                      <SearchableSelect
                        label="Claimant (Optional - can be assigned after creation)"
                        value={claimFormData.claimant_id || ''}
                        onChange={(e) => {
                          const claimantId = e.target.value;
                          if (claimantId) {
                            const claimant = claimants.find(c => c.id === parseInt(claimantId));
                            setSelectedClaimantForClaim(claimant || null);
                          } else {
                            setSelectedClaimantForClaim(null);
                          }
                        }}
                        options={claimants.map(c => {
                          const name = c.first_name && c.last_name 
                            ? `${c.first_name} ${c.last_name}`
                            : c.name || 'Unnamed Claimant';
                          const dob = formatDateOfBirth(c.date_of_birth);
                          const dobText = dob ? ` - DOB: ${dob}` : '';
                          return {
                            value: c.id.toString(),
                            label: `${name}${dobText}`
                          };
                        })}
                        placeholder="Select a claimant (optional)..."
                      />
                      <p className="text-xs text-gray-500 mt-1">You can assign a claimant after creating the claim</p>
                    </div>
                  )}
                  
                  <SearchableSelect
                    label="Case Type"
                    value={claimFormData.case_type}
                    onChange={(e) => setClaimFormData(prev => ({ ...prev, case_type: e.target.value }))}
                    options={caseTypeOptions}
                    placeholder="Select case type"
                    required
                  />
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Claim Number *</label>
                    <input
                      type="text"
                      value={claimFormData.claim_number}
                      onChange={(e) => { setClaimFormData(prev => ({ ...prev, claim_number: e.target.value })); setClaimNumberWarning(null); }}
                      onBlur={checkClaimNumberOnBlur}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Date of Injury</label>
                    <input
                      type="date"
                      value={claimFormData.date_of_injury}
                      onChange={(e) => setClaimFormData(prev => ({ ...prev, date_of_injury: e.target.value }))}
                      onPaste={createDatePasteHandler((e) => setClaimFormData(prev => ({ ...prev, date_of_injury: e.target.value })))}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">CT Start Date</label>
                    <input
                      type="date"
                      value={claimFormData.ct_start_date}
                      onChange={(e) => setClaimFormData(prev => ({ ...prev, ct_start_date: e.target.value }))}
                      onPaste={createDatePasteHandler((e) => setClaimFormData(prev => ({ ...prev, ct_start_date: e.target.value })))}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Continuous Trauma start date"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">CT End Date</label>
                    <input
                      type="date"
                      value={claimFormData.ct_end_date}
                      onChange={(e) => setClaimFormData(prev => ({ ...prev, ct_end_date: e.target.value }))}
                      onPaste={createDatePasteHandler((e) => setClaimFormData(prev => ({ ...prev, ct_end_date: e.target.value })))}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Continuous Trauma end date"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Diagnosis</label>
                    <textarea
                      value={claimFormData.diagnosis}
                      onChange={(e) => setClaimFormData(prev => ({ ...prev, diagnosis: e.target.value }))}
                      placeholder="Enter diagnosis or description"
                      rows={3}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Employer/Insured</label>
                    <input
                      type="text"
                      value={claimFormData.employer_insured}
                      onChange={(e) => setClaimFormData(prev => ({ ...prev, employer_insured: e.target.value }))}
                      placeholder="Enter employer or insured party name (optional)"
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <SearchableSelect
                    label="Contact/Claims Handler"
                    value={claimFormData.contact_claims_handler_id}
                    onChange={(e) => setClaimFormData(prev => ({ ...prev, contact_claims_handler_id: e.target.value }))}
                    options={customers.map(customer => ({ 
                      value: customer.id.toString(), 
                      label: customer.name 
                    }))}
                    placeholder="Select contact/claims handler"
                    required
                  />
                  
                  <SearchableSelect
                    label="Adjuster's Assistant"
                    value={claimFormData.adjusters_assistant_id}
                    onChange={(e) => setClaimFormData(prev => ({ ...prev, adjusters_assistant_id: e.target.value }))}
                    options={customers.map(customer => ({ 
                      value: customer.id.toString(), 
                      label: customer.name 
                    }))}
                    placeholder="Select adjuster's assistant"
                    required
                  />
                  
                  <SearchableSelect
                    label="Nurse Case Manager"
                    value={claimFormData.nurse_case_manager_id}
                    onChange={(e) => setClaimFormData(prev => ({ ...prev, nurse_case_manager_id: e.target.value }))}
                    options={customers.map(customer => ({ 
                      value: customer.id.toString(), 
                      label: customer.name 
                    }))}
                    placeholder="Select nurse case manager (optional)"
                  />
                  
                  <SearchableSelect
                    label="Field Case Manager"
                    value={claimFormData.field_case_manager_id}
                    onChange={(e) => setClaimFormData(prev => ({ ...prev, field_case_manager_id: e.target.value }))}
                    options={customers.map(customer => ({ 
                      value: customer.id.toString(), 
                      label: customer.name 
                    }))}
                    placeholder="Select field case manager (optional)"
                  />
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeClaimModal}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    {editingClaim ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Claimant Details Modal */}
      {showClaimantDetailsModal && selectedClaimantForDetails && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Claimant Details
                </h3>
                <button
                  onClick={closeClaimantDetailsModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Basic Information */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-md font-semibold text-gray-900 flex items-center">
                      <UserIcon className="h-5 w-5 mr-2 text-blue-600" />
                      Basic Information
                    </h4>
                    <button
                      onClick={() => {
                        closeClaimantDetailsModal();
                        openEditModal(selectedClaimantForDetails);
                      }}
                      className="flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <PencilIcon className="h-4 w-4 mr-1" />
                      Edit Claimant
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Name</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {selectedClaimantForDetails.first_name && selectedClaimantForDetails.last_name 
                          ? `${selectedClaimantForDetails.first_name} ${selectedClaimantForDetails.last_name}`
                          : selectedClaimantForDetails.name || 'Unnamed Claimant'
                        }
                      </p>
                    </div>
                    
                    {selectedClaimantForDetails.gender && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Gender</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedClaimantForDetails.gender}</p>
                      </div>
                    )}
                    
                    {selectedClaimantForDetails.date_of_birth && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
                        <p className="mt-1 text-sm text-gray-900 flex items-center">
                          <CalendarIcon className="h-4 w-4 mr-1" />
                          {formatDateOfBirth(selectedClaimantForDetails.date_of_birth)}
                        </p>
                      </div>
                    )}
                    
                    {selectedClaimantForDetails.phone && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Phone</label>
                        <p className="mt-1 text-sm text-gray-900 flex items-center">
                          <PhoneIcon className="h-4 w-4 mr-1" />
                          {selectedClaimantForDetails.phone}
                        </p>
                      </div>
                    )}
                    
                    {selectedClaimantForDetails.language && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Language</label>
                        <p className="mt-1 text-sm text-gray-900 flex items-center">
                          <LanguageIcon className="h-4 w-4 mr-1" />
                          {selectedClaimantForDetails.language}
                        </p>
                      </div>
                    )}
                    
                  </div>
                </div>

                {/* Address Information */}
                {selectedClaimantForDetails.address && (
                  <div>
                    <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                      <MapPinIcon className="h-5 w-5 mr-2 text-blue-600" />
                      Address
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700">Full Address</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedClaimantForDetails.address}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Claims Information */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-md font-semibold text-gray-900 flex items-center">
                      <DocumentTextIcon className="h-5 w-5 mr-2 text-blue-600" />
                      Claims ({selectedClaimantForDetails.claims ? selectedClaimantForDetails.claims.length : 0})
                    </h4>
                    <button
                      onClick={() => {
                        setSelectedClaimantForClaim(selectedClaimantForDetails);
                        setEditingClaim(null);
                        setClaimFormData({
                          case_type: '',
                          claim_number: '',
                          date_of_injury: '',
                          ct_start_date: '',
                          ct_end_date: '',
                          diagnosis: '',
                          contact_claims_handler_id: '',
                          adjusters_assistant_id: '',
                          nurse_case_manager_id: '',
                          field_case_manager_id: '',
                          file_status: 'Open'
                        });
                        setShowClaimModal(true);
                      }}
                      className="flex items-center px-3 py-1 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <PlusIcon className="h-4 w-4 mr-1" />
                      Add Claim
                    </button>
                  </div>
                  {selectedClaimantForDetails.claims && selectedClaimantForDetails.claims.length > 0 && (
                  <div>
                    <p className="text-sm text-gray-600 mb-3">
                      Click on a claim to select it for job request, or use the "Request Job" button to create a general job request.
                    </p>
                    <div className="space-y-2">
                      {selectedClaimantForDetails.claims.map((claim) => (
                        <div 
                          key={claim.id} 
                          className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            selectedClaimForJob && selectedClaimForJob.id === claim.id
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100'
                          }`}
                          onClick={() => {
                            selectClaimForJob(claim);
                            promptIfNeeded(claim);
                          }}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center">
                                <p className="text-sm font-medium text-gray-900">
                                  {claim.claim_number} - {claim.case_type}
                                </p>
                                {selectedClaimForJob && selectedClaimForJob.id === claim.id && (
                                  <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    Selected
                                  </span>
                                )}
                              </div>
                              {claim.date_of_injury && (
                                <p className="text-xs text-gray-600 mt-1">
                                  Date of Injury: {formatDate(claim.date_of_injury)}
                                </p>
                              )}
                              {(claim.ct_start_date || claim.ct_end_date) && (
                                <p className="text-xs text-gray-600 mt-1">
                                  CT Period: {formatDate(claim.ct_start_date) || 'N/A'} - {formatDate(claim.ct_end_date) || 'N/A'}
                                </p>
                              )}
                              {claim.diagnosis && (
                                <p className="text-xs text-gray-600">
                                  Diagnosis: {claim.diagnosis}
                                </p>
                              )}
                            </div>
                            <div className="ml-2 flex space-x-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  requestJobForClaimant(selectedClaimantForDetails, claim, 'interpreting');
                                }}
                                className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-blue-600 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                title="Request Interpreting Service"
                              >
                                <LanguageIcon className="h-3 w-3 mr-1" />
                                Interpreting
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  requestJobForClaimant(selectedClaimantForDetails, claim, 'transportation');
                                }}
                                className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-green-600 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                title="Request Transportation Service"
                              >
                                <TruckIcon className="h-3 w-3 mr-1" />
                                Transportation
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingClaim(claim);
                                  setClaimFormData({
                                    case_type: claim.case_type || '',
                                    claim_number: claim.claim_number || '',
                                    date_of_injury: claim.date_of_injury ? claim.date_of_injury.split('T')[0] : '',
                                    ct_start_date: claim.ct_start_date ? claim.ct_start_date.split('T')[0] : '',
                                    ct_end_date: claim.ct_end_date ? claim.ct_end_date.split('T')[0] : '',
                                    diagnosis: claim.diagnosis || '',
                                    contact_claims_handler_id: claim.contact_claims_handler_id ? claim.contact_claims_handler_id.toString() : '',
                                    adjusters_assistant_id: claim.adjusters_assistant_id ? claim.adjusters_assistant_id.toString() : '',
                                    nurse_case_manager_id: claim.nurse_case_manager_id ? claim.nurse_case_manager_id.toString() : '',
                                    field_case_manager_id: claim.field_case_manager_id ? claim.field_case_manager_id.toString() : ''
                                  });
                                  setSelectedClaimantForClaim(selectedClaimantForDetails);
                                  setShowClaimModal(true);
                                }}
                                className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded"
                                title="Edit Claim"
                              >
                                <PencilIcon className="h-3 w-3" />
                              </button>
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (window.confirm('Are you sure you want to delete this claim?')) {
                                    try {
                                      const data = await makeAuthenticatedRequest(`/claims/${claim.id}`, { method: 'DELETE' });
                                      if (data.success) {
                                        toast.success('Claim deleted successfully');
                                        // Reload claims for this claimant
                                        const updatedClaimantWithClaims = await loadClaimantWithClaims(selectedClaimantForDetails.id, selectedClaimantForDetails);
                                        if (updatedClaimantWithClaims) {
                                          setSelectedClaimantForDetails(prev => ({ ...prev, claims: updatedClaimantWithClaims }));
                                          setClaimants(prev => prev.map(c => 
                                            c.id === selectedClaimantForDetails.id 
                                              ? { ...c, claims: updatedClaimantWithClaims }
                                              : c
                                          ));
                                        }
                                      } else {
                                        toast.error(data.message || 'Failed to delete claim');
                                      }
                                    } catch (error) {
                                      console.error('Error deleting claim:', error);
                                      toast.error(getCustomerPortalErrorMessage(error, 'Failed to delete claim'));
                                    }
                                  }
                                }}
                                className="p-1 text-red-600 hover:text-red-900 hover:bg-red-100 rounded"
                                title="Delete Claim"
                              >
                                <TrashIcon className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  )}
                  {(!selectedClaimantForDetails.claims || selectedClaimantForDetails.claims.length === 0) && (
                    <p className="text-sm text-gray-500">No claims yet. Click "Add Claim" to create one.</p>
                  )}
                </div>

                {/* Past & Upcoming Appointments */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                    <BriefcaseIcon className="h-5 w-5 mr-2 text-blue-600" />
                    Appointments
                  </h4>
                  {loadingClaimantAppointments ? (
                    <div className="flex items-center justify-center py-6">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    </div>
                  ) : (() => {
                    const { upcoming, past } = splitClaimantAppointments(claimantDetailAppointments);
                    if (upcoming.length === 0 && past.length === 0) {
                      return (
                        <p className="text-sm text-gray-500">No appointments found for this claimant.</p>
                      );
                    }
                    return (
                      <div className="space-y-4">
                        <div>
                          <h5 className="text-sm font-medium text-gray-700 mb-2">
                            Upcoming ({upcoming.length})
                          </h5>
                          {upcoming.length === 0 ? (
                            <p className="text-sm text-gray-500">No upcoming appointments.</p>
                          ) : (
                            <div className="space-y-2">
                              {upcoming.map(renderClaimantAppointmentRow)}
                            </div>
                          )}
                        </div>
                        <div>
                          <h5 className="text-sm font-medium text-gray-700 mb-2">
                            Past ({past.length})
                          </h5>
                          {past.length === 0 ? (
                            <p className="text-sm text-gray-500">No past appointments.</p>
                          ) : (
                            <div className="space-y-2">
                              {past.map(renderClaimantAppointmentRow)}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Billing Account */}
                {selectedClaimantForDetails.billing_account_name && (
                  <div>
                    <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                      <BuildingOfficeIcon className="h-5 w-5 mr-2 text-blue-600" />
                      Billing Account
                    </h4>
                    <p className="text-sm text-gray-900">{selectedClaimantForDetails.billing_account_name}</p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between items-center pt-6 border-t border-gray-200 mt-6">
                <button
                  type="button"
                  onClick={closeClaimantDetailsModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Close
                </button>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => requestJobForClaimant(selectedClaimantForDetails, selectedClaimForJob, 'interpreting')}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center"
                  >
                    <LanguageIcon className="h-4 w-4 mr-2" />
                    {selectedClaimForJob 
                      ? `Interpreting for ${selectedClaimForJob.claim_number}` 
                      : 'Request Interpreting'
                    }
                  </button>
                  <button
                    type="button"
                    onClick={() => requestJobForClaimant(selectedClaimantForDetails, selectedClaimForJob, 'transportation')}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 flex items-center"
                  >
                    <TruckIcon className="h-4 w-4 mr-2" />
                    {selectedClaimForJob 
                      ? `Transportation for ${selectedClaimForJob.claim_number}` 
                      : 'Request Transportation'
                    }
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <DuplicateWarningModal
        isOpen={!!duplicateModal}
        type={duplicateModal?.type}
        data={duplicateModal?.data}
        mode={duplicateModal?.mode}
        onClose={() => {
          duplicateModal?.onResolve?.(false);
          setDuplicateModal(null);
        }}
        onConfirm={() => {
          duplicateModal?.onResolve?.(true);
          setDuplicateModal(null);
        }}
        onViewClaimant={handleViewExistingClaimant}
        onViewClaim={viewClaim}
      />

      <AdjusterConfirmModal
        isOpen={!!pendingClaim}
        claim={pendingClaim}
        onSave={saveAssignment}
        onSkip={skipPrompt}
        saving={savingRole}
      />
    </div>
  );
};

export default Claimants;
